<template>
  <div class="step-card">
    <div class="step-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">Where do you teach?</h2>
        <p class="step-subtitle">
          Select your school to personalize your curriculum
        </p>
      </div>

      <!-- School Input -->
      <div class="input-container">
        <SchoolSelectionInput v-model="selectedSchool" />
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <ActionButton @click="handleBack" variant="outline" class="back-button"
          >Back</ActionButton
        >
        <div class="right-buttons">
          <ActionButton @click="handleSkip" variant="text" class="skip-button"
            >Skip</ActionButton
          >
          <ActionButton
            :disabled="!selectedSchool"
            @click="handleNext"
            class="next-button"
            >Next
          </ActionButton>
        </div>
      </div>
    </div>
  </div>
  <ActionButton @click="handleSkip" variant="text" class="skip-button-mobile"
    >Skip</ActionButton
  >
</template>

<script setup>
/* eslint-disable */
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  defineEmits,
  defineProps,
  watch,
} from "vue";
import ActionButton from "../common/ActionButton.vue";
import userWalkthroughService from "@/services/userWalkthroughService";
import SchoolSelectionInput from "../common/SchoolSelectionInput.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["next", "back", "skip", "update:modelValue"]);

const selectedSchool = ref(props.modelValue);

// Watch for prop changes (when navigating back)
watch(
  () => props.modelValue,
  (newValue) => {
    // Only update if the value actually changed to prevent loops
    const currentId = selectedSchool.value?.id;
    const newId = newValue?.id;
    const currentName = selectedSchool.value?.name;
    const newName = newValue?.name;

    if (currentId !== newId || currentName !== newName) {
      if (newValue) {
        selectedSchool.value = newValue;
      } else {
        selectedSchool.value = null;
      }
    }
  },
  { immediate: true }
);

// Watch for local selection changes to emit update
watch(selectedSchool, (newValue) => {
  emit("update:modelValue", newValue);
});

const handleNext = () => {
  // If we have a selectedSchool, emit next
  if (selectedSchool.value) {
    emit("next", { school: selectedSchool.value });
  } else {
    // NOTE: Original logic had "showAddSchoolOption" check here.
    // Since the input handles "Add School" by setting selectedSchool to a new object,
    // we just need to check if selectedSchool is truthy.
    // However, the original code had a subtle logic where you could click next if "Add school" option *was visible*?
    // No, "handleAddSchool" was triggered by mousedown on the option.
    // But handleNext had: if (selectedSchool.value || showAddSchoolOption.value)
    // I'll assume standard flow: user must select something (either existing or "add new" from dropdown)
  }
};

const handleBack = () => {
  emit("back");
};

const handleSkip = () => {
  emit("skip");
};
</script>

<style scoped>
.step-card {
  background-color: white;
  border: 1px solid #0478ce;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-end;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #3b4648;
}

.step-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.step-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.skip-button-mobile {
  display: none;
}

/* Input Container */
.input-container {
  width: 100%;
  position: relative;
}

/* Button Container */
.button-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.back-button {
  background-color: white;
  border: 1px solid #d8dae5;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  min-width: 100px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
}

.next-button {
  width: 100px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.skip-button {
  width: 100px;
  background-color: #f2f3fb;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.skip-button:hover {
  color: #3b4648;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-card {
    min-height: auto;
  }

  .button-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .right-buttons {
    width: 100%;
  }

  .back-button {
    margin-bottom: 0px;
  }

  .skip-button {
    display: none;
  }

  .skip-button-mobile {
    margin-top: 10px;
    display: block;
  }

  .next-button {
    width: 100%;
  }
}
</style>
